'use client';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { ComponentProps, ReactNode, useCallback, useState } from 'react';
import { SingleTestimonial } from './singleTestimonial';
import classNames from '@wilkins-software/classnames-tailwind-merge';

const TESTIMONIAL_SLIDER_ELEMENT_ID = 'testimonial-slider';

function Slider(props: { children: ReactNode; slide: number }) {
  return (
    <div
      className="overflow-hidden px-4"
      id={TESTIMONIAL_SLIDER_ELEMENT_ID}
      aria-live="polite"
    >
      <div
        className="flex items-center gap-6 py-4"
        style={{
          transform: `translateX(-${props.slide * 100}%)`,
          transition: 'transform 0.5s',
        }}
      >
        {props.children}
      </div>
    </div>
  );
}

function NextTestimonialButton(props: {
  onClick: () => void;
  classNames?: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label="Show next testimonial"
      aria-controls={TESTIMONIAL_SLIDER_ELEMENT_ID}
      className={classNames(
        props.classNames,
        'right-8 top-1/2 z-20 cursor-pointer rounded-full p-2 text-slate-600 transition-colors hover:bg-gray-300 hover:text-slate-900 md:right-0 lg:block',
      )}
    >
      <ChevronRight
        className="size-12 xl:size-24"
        aria-hidden
        focusable="false"
      />
    </button>
  );
}

function PrevTestimonialButton(props: {
  onClick: () => void;
  classNames?: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label="Show previous testimonial"
      aria-controls={TESTIMONIAL_SLIDER_ELEMENT_ID}
      className={classNames(
        props.classNames,
        'left-8 top-1/2 z-20 cursor-pointer rounded-full p-2 text-slate-600 transition-colors hover:bg-gray-300 hover:text-slate-900 md:left-0 lg:block',
      )}
    >
      <ChevronLeft
        className="size-12 xl:size-24"
        aria-hidden
        focusable="false"
      />
    </button>
  );
}

function MobileButtons(props: { onClick: () => void; onClick1: () => void }) {
  return (
    <div className="mx-auto flex justify-center pb-8 lg:hidden">
      <PrevTestimonialButton onClick={props.onClick} />
      <NextTestimonialButton onClick={props.onClick1} />
    </div>
  );
}

export const Testimonials = ({
  title,
  subTitle,
  description,
  testimonials,
}: {
  title: string;
  subTitle: string;
  description: string;
  testimonials: ComponentProps<typeof SingleTestimonial>[];
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const handlePrevClick = useCallback(() => {
    const nextValue = currentSlide - 1;
    if (nextValue < 0) {
      return setCurrentSlide(testimonials.length - 1);
    }
    setCurrentSlide(nextValue);
  }, [currentSlide, testimonials.length]);
  const handleNextClick = useCallback(() => {
    const nextValue = currentSlide + 1;
    if (nextValue >= testimonials.length) {
      return setCurrentSlide(0);
    }
    setCurrentSlide(nextValue);
  }, [currentSlide, testimonials.length]);
  return (
    <section id="testimonial" className="homepage-section-vertical-padding">
      <div className="homepage-wrapper">
        <div className="mx-auto w-full lg:w-8/12">
          <div className="mb-8 text-center">
            {/* Eyebrow above the section heading — styled like a heading but
                deliberately not one, so the h1 → h2 order stays sequential. */}
            <p className="text-title spacing-title">{title}</p>
            <h2 className="text-subtitle spacing-subtitle">{subTitle}</h2>
            <p className="text-description spacing-description mx-auto">
              {description}
            </p>
          </div>
        </div>

        <div className="relative">
          <PrevTestimonialButton
            classNames="absolute hidden -translate-y-1/2"
            onClick={handlePrevClick}
          />
          <Slider slide={currentSlide}>
            {testimonials.map((testimonial) => (
              <SingleTestimonial key={testimonial.name} {...testimonial} />
            ))}
          </Slider>
          <NextTestimonialButton
            classNames="absolute hidden -translate-y-1/2"
            onClick={handleNextClick}
          />
        </div>
        <MobileButtons onClick={handlePrevClick} onClick1={handleNextClick} />
      </div>
    </section>
  );
};
